import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import novasLogo from '../../assets/novas-logo.png';
import { getHomework } from '../../utils/homeworkService';
import { getStudentByInviteSlug } from '../../utils/studentsService';
import './Homework.css';

export default function Homework({ studentData }) {
  const navigate = useNavigate();
  const { studentSlug } = useParams();
  // Get student identifier for loading homework
  const getStudentIdentifier = () => {
    const studentId = window.location.pathname.split('/').pop();
    if (studentId && studentId !== 'homework') {
      return studentId;
    }
    let studentIdentifier = studentData?.slug || studentData?.originalSlug;
    if (!studentIdentifier && studentData?.inviteLink) {
      const inviteLinkParts = studentData.inviteLink.split('/');
      const lastPart = inviteLinkParts[inviteLinkParts.length - 1];
      if (lastPart && lastPart.includes('-')) {
        studentIdentifier = lastPart.split('-')[0];
      }
    }
    return studentIdentifier || 'unknown';
  };

  const [homeworkList, setHomeworkList] = useState([]);
  const [homeworkLoading, setHomeworkLoading] = useState(true);
  const [homeworkFilter, setHomeworkFilter] = useState('new'); // 'new', 'submitted', 'reviewed'

  // Load homework assignments from Supabase and localStorage
  const loadHomework = async () => {
    setHomeworkLoading(true);
    const studentIdentifier = getStudentIdentifier();
    console.log('📝 Homework.js - loadHomework called, studentIdentifier:', studentIdentifier);
    let allHomework = [];
    
    try {
      // Пытаемся получить UUID студента из Supabase
      let studentUuid = null;
      try {
        console.log('📝 Homework.js - Getting student by invite slug:', studentIdentifier);
        const student = await getStudentByInviteSlug(studentIdentifier);
        console.log('📝 Homework.js - Student from Supabase:', student);
        if (student && student.id) {
          studentUuid = student.id;
          console.log('📝 Homework.js - Student UUID:', studentUuid);
        } else {
          console.warn('📝 Homework.js - Student found but no ID:', student);
        }
      } catch (e) {
        console.error('📝 Homework.js - Could not get student from Supabase:', e);
      }
      
      // Загружаем из Supabase, если есть UUID
      if (studentUuid) {
        try {
          console.log('📝 Homework.js - Loading homework for student UUID:', studentUuid);
          const supabaseHomework = await getHomework(studentUuid);
          console.log('📝 Homework.js - Raw homework from getHomework:', supabaseHomework);
          console.log('📝 Homework.js - Homework count:', supabaseHomework?.length || 0);
          
          if (supabaseHomework && supabaseHomework.length > 0) {
            // Загружаем все homework, фильтрация будет в UI
            allHomework = supabaseHomework;
            console.log('📝 Homework.js - All homework loaded:', allHomework);
            console.log('📝 Homework.js - Total count:', allHomework.length);
          } else {
            console.warn('📝 Homework.js - No homework returned from getHomework or empty array');
          }
        } catch (e) {
          console.error('📝 Homework.js - Error loading homework from Supabase:', e);
          console.error('📝 Homework.js - Error details:', {
            message: e.message,
            stack: e.stack,
            studentUuid
          });
        }
      } else {
        console.warn('📝 Homework.js - No student UUID, cannot load homework from Supabase');
      }
      
      // Не загружаем из localStorage - только из Supabase
    } catch (error) {
      console.error('📝 Homework.js - Error in loadHomework:', error);
      setHomeworkLoading(false);
      return;
    }
    
    // Дедупликация по ID (на случай, если все же есть дубликаты)
    const uniqueHomework = [];
    const seenIds = new Set();
    for (const hw of allHomework) {
      const hwId = String(hw.id);
      if (!seenIds.has(hwId)) {
        seenIds.add(hwId);
        uniqueHomework.push(hw);
      }
    }
    
    // Sort by assignedAt (newest first)
    uniqueHomework.sort((a, b) => {
      const dateA = new Date(a.assignedAt || a.created_at || 0);
      const dateB = new Date(b.assignedAt || b.created_at || 0);
      return dateB - dateA;
    });
    
    setHomeworkList(uniqueHomework);
    setHomeworkLoading(false);
  };

  useEffect(() => {
    loadHomework();
    
    // Listen for homework updates
    const handleHomeworkUpdate = () => {
      loadHomework();
    };
    
    window.addEventListener('homeworkUpdated', handleHomeworkUpdate);
    
    // Also listen to storage events (for cross-tab updates)
    window.addEventListener('storage', (e) => {
      if (e.key && e.key.startsWith('homework_')) {
        loadHomework();
      }
    });
    
    return () => {
      window.removeEventListener('homeworkUpdated', handleHomeworkUpdate);
      window.removeEventListener('storage', () => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  const handleHomeworkClick = (homework) => {
    // Navigate to the lesson that contains this homework
    const currentSlug = studentSlug || getStudentIdentifier();
    const lessonId = homework.lessonId;
    
    if (lessonId && lessonId !== 'new') {
      // Navigate to lesson with homeworkId parameter and fromHomework flag to allow editing
      navigate(`/student-dashboard/${currentSlug}/lesson/${lessonId}?homeworkId=${homework.id}&fromHomework=true`);
    } else {
      // Fallback: try to find lesson from homework data or show error
      console.warn('Homework does not have a valid lessonId:', homework);
      alert('Unable to open homework: lesson not found');
    }
  };

  // Filter homework based on selected filter
  const filteredHomework = homeworkList.filter(homework => {
    if (homeworkFilter === 'new') {
      return homework.status === 'assigned';
    } else if (homeworkFilter === 'submitted') {
      return homework.status === 'submitted';
    } else if (homeworkFilter === 'reviewed') {
      return homework.status === 'reviewed';
    }
    return true;
  });

  return (
    <div className="homework-page">
      <div className="homework-banner">
        <div className="homework-banner-content">
          <h2 className="homework-title-banner">Homework</h2>
          <p className="homework-subtitle">Your assignments and tasks</p>
        </div>
        
        <div className="homework-novas-banner-logo">
          <img src={novasLogo} alt="Novas Logo" />
        </div>
      </div>

      <div className="homework-content">
        {/* Filter buttons */}
        <div className="homework-filter-bar">
          <button
            className={`homework-filter-btn ${homeworkFilter === 'new' ? 'active' : ''}`}
            onClick={() => setHomeworkFilter('new')}
          >
            New
          </button>
          <button
            className={`homework-filter-btn ${homeworkFilter === 'submitted' ? 'active' : ''}`}
            onClick={() => setHomeworkFilter('submitted')}
          >
            Submitted
          </button>
          <button
            className={`homework-filter-btn ${homeworkFilter === 'reviewed' ? 'active' : ''}`}
            onClick={() => setHomeworkFilter('reviewed')}
          >
            Reviewed
          </button>
        </div>

        <div className="homework-cards-grid">
          {homeworkLoading ? (
            <div className="homework-loading">
              <span className="homework-loading-spinner" aria-hidden />
              <span>Loading homework…</span>
            </div>
          ) : filteredHomework.length > 0 ? (
            filteredHomework.map((homework) => (
              <div 
                key={homework.id} 
                className="homework-assignment-card"
                onClick={() => handleHomeworkClick(homework)}
              >
                <div className="homework-card-info">
                  <h3 className="homework-card-title">
                    {homework.title || 'Untitled Homework'}
                  </h3>
                  <div className="homework-card-due-date">
                    Due: {formatDate(homework.dueDate)}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="homework-empty-message">
              <p>
                {homeworkFilter === 'new' && 'No new homework assignments.'}
                {homeworkFilter === 'submitted' && 'No submitted homework yet.'}
                {homeworkFilter === 'reviewed' && 'No reviewed homework yet.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

